import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import dataService from "../../services/dataService";
import { ShipTo } from "../../models/warehouse/types";
import ShipToMapComponent from "./shipToMapComponent";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useDispatch } from "react-redux";
import { closeShipToDialog } from "../../store/slices/shiptoSlice";
import MapTypeEnum from "../../../enums/mapTypeEnum";
import ShipToConfirmationDialog from "./shipToConfirmationDialog";
import { getUserProfileDataAsync } from "@/app/store/slices/userSlice";

interface ShipToDialogProps {
  open: boolean;
  onClose: () => void;
}

const ShipToDialog: React.FC<ShipToDialogProps> = ({ open, onClose }) => {
  const [shipTos, setShipTos] = useState<ShipTo[]>([]);
  const [shipToMapModel, setShipToMapModel] = useState<ShipTo[]>([]);
  const [shipToRows, setShipToRows] = useState<GridRowsProp>([]);
  const [selectedShipToRow, setSelectedShipToRow] = useState<GridRowsProp>([]);
  const [searchTmpQuery, setSearchTmpQuery] = useState<string>("");
  const [mapType, setMapType] = useState<MapTypeEnum>(MapTypeEnum.SearchText);
  const [searchTmpQueryChanged, setSearchTmpQueryChanged] =
    useState<boolean>(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const [shipToConfirmationOpen, setShipToConfirmationOpen] = useState(false);
  const [shipToConfirmationDetails, setShipToConfirmatioDetails] = useState<{
    currentShipToName?: string;
    branchName?: string;
  } | null>(null);

  const [isConfirmingShipTo, setIsConfirmingShipTo] = useState(false);
  const [newShipToId, setNewShipToId] = useState<number | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    const getShipTos = async () => {
      await fetchShipTos();
    };
    getShipTos();
  }, [searchTmpQueryChanged]);

  const fetchShipTos = async () => {
    const response = await dataService.getShipTos(searchTmpQuery);
    if (response.success === true) {
      var data = response.data;
      setShipTos(data);
      const shipTosMap = data.filter(
        (shipTo) => shipTo.isSelected || data.indexOf(shipTo) < 5
      );
      setShipToMapModel(shipTosMap);
      const shipToRows = data.map((x: ShipTo) => ({
        id: x.id,
        name: x.fullName,
        address: x.fullAddress,
        cityStateZip: x.cityStateZip,
        fullName: x.fullName,
        address1: x.address,
        isSelected: x.isSelected,
        options: "",
      }));
      setShipToRows(shipToRows.filter((shipTo) => !shipTo.isSelected));
      setSelectedShipToRow(shipToRows.filter((shipTo) => shipTo.isSelected));
    } else {
      console.error("Error fetching ship tos");
    }
  };

  const handleShipToSelect = async (selectedShipToId: number) => {
    const response = await dataService.validateShipToChange(selectedShipToId);
    const validation = response.data;

    setNewShipToId(selectedShipToId);

    if (validation.needsUserConfirmation) {
      setConfirmationMessage(validation.message ?? "");
      setPendingSapConfirmation(
        validation.needsSapConfirmation
          ? {
              currentShipToName: validation.currentShipToName,
              branchName: validation.warehouseName,
            }
          : null
      );
      setConfirmationOpen(true);
      return;
    }

    if (validation.needsSapConfirmation) {
      setShipToConfirmatioDetails({
        currentShipToName: validation.currentShipToName,
        branchName: validation.warehouseName,
      });
      setShipToConfirmationOpen(true);
      return;
    }

    await applyShipToChange(selectedShipToId);
  };

  const [pendingSapConfirmation, setPendingSapConfirmation] = useState<{
    currentShipToName?: string;
    branchName?: string;
  } | null>(null);

  const handleConfirmationClose = async (
    confirmed: boolean,
    shipToId: number
  ) => {
    setConfirmationOpen(false);

    if (!confirmed) {
      setPendingSapConfirmation(null);
      return;
    }

    if (pendingSapConfirmation) {
      setShipToConfirmatioDetails(pendingSapConfirmation);
      setPendingSapConfirmation(null);
      setShipToConfirmationOpen(true);
      return;
    }

    await applyShipToChange(shipToId);
  };

  const handleShipToConfirmationClose = async (confirmed: boolean) => {
    setShipToConfirmationOpen(false);
    setShipToConfirmatioDetails(null);

    if (!confirmed || newShipToId === null) return;

    const response = await dataService.validateShipToChange(newShipToId);
    const validation = response.data;

    if (validation.needsUserConfirmation) {
      setConfirmationMessage(validation.message ?? "");
      setConfirmationOpen(true);
      return;
    }

    await applyShipToChange(newShipToId);
  };

  const applyShipToChange = async (shipToId: number) => {
    setIsConfirmingShipTo(true);
    try {
      const response = await dataService.setSelectedShipTo(shipToId.toString());
      if (response.success === true) {
        handleDialogClose();
        window.location.reload();
      } else {
        console.error("Error setting ship to");
      }
    } finally {
      setIsConfirmingShipTo(false);
    }
  };

  const handleDialogClose = () => {
    onClose();
    handleClear();
    dispatch(closeShipToDialog());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setMapType(MapTypeEnum.SearchText);
    }
  };

  const handleClear = () => {
    if (searchTmpQuery !== "") {
      setSearchTmpQuery("");
    }
    setSearchTmpQueryChanged(!searchTmpQueryChanged);
    setMapType(MapTypeEnum.SearchText);
  };

  const handleSearch = () => {
    setMapType(MapTypeEnum.SearchText);
    setSearchTmpQueryChanged(!searchTmpQueryChanged);
  };

  const onUseCurrentLocation = () => {
    setMapType(MapTypeEnum.UseMyCurrentLocation);
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 2 },
    {
      field: "address",
      headerName: "Address",
      flex: 2,
      renderCell: (params) => {
        return (
          <div key={params.value} style={{ whiteSpace: "pre-line" }}>
            <Typography key={params.row.address1}>
              {params.row.address1}
            </Typography>
            <Typography key={params.row.cityStateZip}>
              {params.row.cityStateZip}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "options",
      headerName: "Options",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return params.row.isSelected ? (
          <Button
            variant="outlined"
            color="primary"
            className="whiteButton"
            disabled={true}
          >
            Selected
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            className="blueButtonOutline"
            onClick={() => handleShipToSelect(params.row.id)}
          >
            Select
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        className="warehouseModal"
      >
        <DialogContent sx={{ position: "relative" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} textAlign={"center"}>
              <Button onClick={handleDialogClose} style={{ float: "right" }}>
                <CloseSharpIcon />
              </Button>
            </Grid>
            <Grid item md={4} xs={12} sx={{ mb: { xs: 0, sm: 10 } }}>
              <>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    marginTop: "0px",
                  }}
                >
                  CURRENT SHIP TO
                </h2>
                <h4 style={{ margin: 0 }}>{selectedShipToRow[0]?.fullName}</h4>
                <Typography sx={{ my: 0 }}>
                  {selectedShipToRow[0]?.address1}
                </Typography>
                <Typography sx={{ my: 0 }}>
                  {selectedShipToRow[0]?.cityStateZip}
                </Typography>
                <p style={{ fontSize: "80%", marginTop: "10px" }}></p>
              </>

              <div className="shipToDialogMapContainer">
                {shipToMapModel && (
                  <ShipToMapComponent
                    mapKey="SHIPTO_MAP_DIALOG"
                    longitudeCenterOffset={0}
                    disableControls={false}
                    mapType={mapType}
                    shipTos={shipToMapModel}
                  />
                )}
              </div>
            </Grid>
            <Grid item md={8} xs={12} sx={{ mb: { xs: 0, sm: 10 } }}>
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  marginTop: "0px",
                }}
              >
                YOUR OTHER SHIP TO'S
              </h2>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  variant="outlined"
                  type="text"
                  value={searchTmpQuery}
                  className="mapSearchInput"
                  onChange={(e) => setSearchTmpQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search Ship To's"
                  style={{ marginRight: "10px", marginBottom: "10px" }}
                  autoComplete="off"
                />
                <Button
                  className="blueButtonOutline mapSearchButton"
                  onClick={handleSearch}
                  sx={{ mr: 1, px: 5 }}
                >
                  Search
                </Button>
                <Button
                  className="blueButtonOutline mapSearchButton"
                  sx={{ px: 5 }}
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </Grid>
              <Button
                size="small"
                sx={{ my: 1 }}
                style={{ fontSize: "75%" }}
                variant="text"
                className="mapUseCurrentLocation"
                onClick={onUseCurrentLocation}
              >
                <MyLocationIcon fontSize="small" sx={{ mr: 1 }} /> Use My
                Current Location
              </Button>
              <Grid item xs={12}>
                <DataGrid
                  rows={shipToRows}
                  sx={{ mt: 3 }}
                  columns={columns}
                  getRowHeight={() => "auto"}
                  disableColumnSorting={true}
                  disableColumnFilter={true}
                  disableColumnMenu={true}
                  autoHeight
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5, 10, 20]}
                  onPaginationModelChange={(params) => {
                    const newPageSize = params.pageSize;
                    const newPage = params.page;
                    const paginatedRows = shipToRows.slice(
                      newPage * newPageSize,
                      (newPage + 1) * newPageSize
                    );
                    const paginatedShipTos: ShipTo[] = paginatedRows.map(
                      (row) => shipTos.find((shipTo) => shipTo.id === row.id)!
                    );
                    const selectedShipTo = shipTos.filter(
                      (shipTo) => shipTo.isSelected
                    );
                    const fullShipTos = paginatedShipTos.concat(selectedShipTo);
                    setShipToMapModel(fullShipTos);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>{confirmationMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            className="blueButtonOutline"
            onClick={() =>
              newShipToId !== null && handleConfirmationClose(true, newShipToId)
            }
            autoFocus
          >
            CONTINUE
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className="blueButtonOutline"
            onClick={() =>
              newShipToId !== null &&
              handleConfirmationClose(false, newShipToId)
            }
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      <ShipToConfirmationDialog
        open={shipToConfirmationOpen}
        details={shipToConfirmationDetails}
        isSubmitting={isConfirmingShipTo}
        onConfirm={() => handleShipToConfirmationClose(true)}
        onCancel={() => handleShipToConfirmationClose(false)}
      />

      {isConfirmingShipTo && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2500,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      )}
    </>
  );
};

export default ShipToDialog;
